import {
  Button,
  Checkbox,
  Field,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Input,
  RadioGroup,
  RadioGroupItem,
} from "@colorglo/ui/components";
import { useId, useState } from "react";
import { toast } from "sonner";
import {
  type HourSpan,
  type ResourceView,
  useSaveResourceHours,
  useSaveResourceSkills,
  useServiceSettings,
  useUpdateResource,
} from "../../api/settings.ts";
import { useLocale, useT } from "../../lib/locale.ts";
import { weekdayName } from "../../lib/time.ts";
import { SettingsCard, SettingsSection } from "./shell.tsx";

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

export function ColorPicker({
  palette,
  value,
  onChange,
  label,
}: {
  readonly palette: readonly string[];
  readonly value: string;
  readonly onChange: (color: string) => void;
  readonly label: string;
}) {
  const id = useId();

  return (
    <FieldSet>
      <FieldLegend variant="label">{label}</FieldLegend>
      <RadioGroup value={value} onValueChange={onChange} className="flex flex-row flex-wrap gap-2">
        {palette.map((color) => (
          <FieldLabel
            key={color}
            htmlFor={`${id}-${color}`}
            className="cursor-pointer rounded-sm border-2 border-transparent p-0 has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-transparent"
          >
            <RadioGroupItem id={`${id}-${color}`} value={color} className="sr-only" />
            <span
              aria-hidden="true"
              className="block size-11 rounded-sm"
              style={{ backgroundColor: color }}
            />
            <span className="sr-only">{color}</span>
          </FieldLabel>
        ))}
      </RadioGroup>
    </FieldSet>
  );
}

export function StaffSchedule({
  franchiseId,
  resource,
  palette,
  editable,
}: {
  readonly franchiseId: string;
  readonly resource: ResourceView;
  readonly palette: readonly string[];
  readonly editable: boolean;
}) {
  const t = useT();
  const locale = useLocale();
  const services = useServiceSettings(franchiseId);

  const update = useUpdateResource(franchiseId);
  const saveHours = useSaveResourceHours(franchiseId);
  const saveSkills = useSaveResourceSkills(franchiseId);

  const [name, setName] = useState(resource.name);
  const [color, setColor] = useState(resource.color);
  const [bookable, setBookable] = useState(resource.is_bookable);
  const [inherits, setInherits] = useState(resource.inherits_hours);
  const [hours, setHours] = useState<readonly HourSpan[]>(resource.hours);
  const [skills, setSkills] = useState<readonly string[]>(resource.skills);
  const id = useId();

  const onSave = () => {
    update.mutate(
      {
        resourceId: resource.id,
        name,
        color,
        is_bookable: bookable,
        inherits_hours: inherits,
      },
      {
        onError: () => toast.error(t("settings.error")),
        onSuccess: () => {
          saveSkills.mutate({ resourceId: resource.id, serviceIds: skills });
          if (!inherits) saveHours.mutate({ resourceId: resource.id, hours });
          toast.success(t("settings.staff.account.saved"));
        },
      },
    );
  };

  const spansOn = (weekday: number) => hours.filter((span) => span.weekday === weekday);

  const replaceDay = (weekday: number, spans: readonly HourSpan[]) => {
    setHours((current) => [...current.filter((span) => span.weekday !== weekday), ...spans]);
  };

  return (
    <SettingsSection
      heading={t("settings.staff.schedule.heading")}
      body={t("settings.staff.schedule.body", { name: resource.name })}
    >
      <SettingsCard>
        <Field>
          <FieldLabel htmlFor={`${id}-name`}>
            {t("settings.staff.schedule.calendar_name")}
          </FieldLabel>
          <Input
            id={`${id}-name`}
            type="text"
            className="max-w-sm"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <ColorPicker
          palette={palette}
          value={color}
          onChange={setColor}
          label={t("settings.staff.color")}
        />

        <Field orientation="horizontal">
          <Checkbox
            id={`${id}-bookable`}
            checked={bookable}
            onCheckedChange={(next) => setBookable(next === true)}
          />
          <FieldLabel htmlFor={`${id}-bookable`} className="font-normal">
            {t("settings.staff.bookable")}
          </FieldLabel>
        </Field>

        <Field orientation="horizontal">
          <Checkbox
            id={`${id}-inherits`}
            checked={inherits}
            onCheckedChange={(next) => setInherits(next === true)}
          />
          <FieldLabel htmlFor={`${id}-inherits`} className="font-normal">
            {t("settings.staff.inherits")}
          </FieldLabel>
        </Field>

        {!inherits && (
          <FieldSet>
            <FieldLegend variant="label">{t("settings.staff.own_hours")}</FieldLegend>
            <FieldGroup className="gap-3">
              {WEEKDAYS.map((weekday) => {
                const span = spansOn(weekday)[0];
                return (
                  <div key={weekday} className="flex flex-wrap items-end gap-3">
                    <Field orientation="horizontal" className="w-40">
                      <Checkbox
                        id={`${id}-day-${weekday}`}
                        checked={span !== undefined}
                        onCheckedChange={(next) =>
                          replaceDay(
                            weekday,
                            next === true
                              ? [{ weekday, start_time: "08:00", end_time: "17:00" }]
                              : [],
                          )
                        }
                      />
                      <FieldLabel htmlFor={`${id}-day-${weekday}`} className="font-normal">
                        {weekdayName(weekday, locale)}
                      </FieldLabel>
                    </Field>

                    {span !== undefined && (
                      <>
                        <Field className="w-32">
                          <FieldLabel htmlFor={`${id}-from-${weekday}`}>
                            {t("settings.hours.from")}
                          </FieldLabel>
                          <Input
                            id={`${id}-from-${weekday}`}
                            type="time"
                            value={span.start_time}
                            onChange={(event) =>
                              replaceDay(weekday, [{ ...span, start_time: event.target.value }])
                            }
                          />
                        </Field>
                        <Field className="w-32">
                          <FieldLabel htmlFor={`${id}-to-${weekday}`}>
                            {t("settings.hours.to")}
                          </FieldLabel>
                          <Input
                            id={`${id}-to-${weekday}`}
                            type="time"
                            value={span.end_time}
                            onChange={(event) =>
                              replaceDay(weekday, [{ ...span, end_time: event.target.value }])
                            }
                          />
                        </Field>
                      </>
                    )}
                  </div>
                );
              })}
            </FieldGroup>
          </FieldSet>
        )}

        <FieldSet>
          <FieldLegend variant="label">{t("settings.staff.skills")}</FieldLegend>
          <FieldGroup className="gap-2">
            {(services.data?.data ?? [])
              .filter((service) => service.enabled)
              .map((service) => (
                <Field key={service.id} orientation="horizontal">
                  <Checkbox
                    id={`${id}-skill-${service.id}`}
                    checked={skills.includes(service.id)}
                    onCheckedChange={(next) =>
                      setSkills((current) =>
                        next === true
                          ? [...current, service.id]
                          : current.filter((entry) => entry !== service.id),
                      )
                    }
                  />
                  <FieldLabel htmlFor={`${id}-skill-${service.id}`} className="font-normal">
                    {service.name[locale] ?? service.name.sv}
                  </FieldLabel>
                </Field>
              ))}
            {skills.length === 0 && (
              <p className="text-body-sm text-muted-foreground">
                {t("settings.staff.skills_empty")}
              </p>
            )}
          </FieldGroup>
        </FieldSet>

        {editable && (
          <div>
            <Button onClick={onSave} disabled={update.isPending || name === ""}>
              {t("settings.save")}
            </Button>
          </div>
        )}
      </SettingsCard>
    </SettingsSection>
  );
}
